import checkGreenIcon from '../../../../assets/check-green.svg';
import { pencilIcon, plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import styles from './CustomerDetailsContacts.module.scss';
import { useMemo, useState } from 'react';
import CustomerContact from './CustomerContact';
import { ICustomerContact, NewCustomerContact } from '../../../../model';
import CommonModal from '../../../common/modal/CommonModal';
import {usePermissions} from "../../../auth/PermissionsProvider";

interface Props {
  edit?: boolean;
  isNew?: boolean | undefined;
  formRenderProps: any;
}

const CustomerDetailsContacts = (props: Props) => {
  const { formRenderProps, edit, isNew } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState<number>();
  const [editMode, setEditMode] = useState(false);
  const [originalContact, setOriginalContact] = useState<ICustomerContact | null>(null);
  const { hasEditAccess } = usePermissions();

  let customerContacts = formRenderProps.valueGetter('customerContacts');
  const addContact = () => {
    const newContactList = [...customerContacts, NewCustomerContact()];
    const newContactIndex = newContactList.length - 1;

    formRenderProps.onChange('customerContacts', { value: newContactList });

    setEditingContactIndex(newContactIndex);
    setEditMode(false);
    setIsModalOpen(true);
  };
  const editContact = (contact: ICustomerContact) => {
    const contactIndex = customerContacts.indexOf(contact);
    setEditingContactIndex(contactIndex);
    setEditMode(true);
    setOriginalContact({ ...contact });
    setIsModalOpen(true);
  };

  const removeContact = (contactToRemove: ICustomerContact) => {
    const currentContacts = formRenderProps.valueGetter('customerContacts') || [];

    const updatedContacts = currentContacts.filter((contact: ICustomerContact) => contact.id !== contactToRemove.id);

    formRenderProps.onChange('customerContacts', { value: updatedContacts });

    setTimeout(() => {
      if (formRenderProps && formRenderProps.onSubmit) {
        formRenderProps.onSubmit();
      }
    }, 0);
  };

  const handleCloseModal = () => {
    if (!editMode) {
      const updatedContacts = formRenderProps.valueGetter('customerContacts').slice(0, -1);
      formRenderProps.onChange('customerContacts', { value: updatedContacts });
    } else {
      if (editingContactIndex !== undefined && originalContact) {

        const currentContacts = formRenderProps.valueGetter('customerContacts') || [];

        const updatedContacts = [...currentContacts];

        updatedContacts[editingContactIndex] = originalContact;

        formRenderProps.onChange('customerContacts', { value: updatedContacts });
      }
    }
    setIsModalOpen(false);
  };

  const handleSaveChanges = () => {
    setIsModalOpen(false);
    if (isNew) {
      const preUpdatedContacts = formRenderProps.valueGetter('customerContacts');
      formRenderProps.onChange('customerContacts', { value: preUpdatedContacts });
      customerContacts = [...preUpdatedContacts];
    }

    if (!isNew && formRenderProps && formRenderProps.onSubmit) {
      formRenderProps.onSubmit();
    }
  };

  const allContactsInForm = formRenderProps.valueGetter('customerContacts') || [];
  const contactForModal = editingContactIndex !== undefined ? allContactsInForm[editingContactIndex] : null;

  const isModalFormValid = useMemo(() => {
    if (editingContactIndex === undefined) {
      return false;
    }

    const errors = formRenderProps.errors;

    const allContacts = formRenderProps.valueGetter('customerContacts') || [];
    const currentContact = allContacts[editingContactIndex];

    if (!currentContact) return false;

    const fieldPrefix = `customerContacts[${editingContactIndex}]`;
    const hasErrors = Object.keys(errors).some((key) => key.startsWith(fieldPrefix) && errors[key]);

    const requiredFieldsFilled = currentContact.firstName && currentContact.lastName;

    return requiredFieldsFilled && !hasErrors;
  }, [formRenderProps, editingContactIndex]);

  return (
    <>
      <div className={styles.headerBlock}>
        {hasEditAccess && <Button type={'button'} svgIcon={plusIcon} onClick={() => addContact()}>
          Add
        </Button>}

      </div>

      <table style={{ padding: 0 }} className={'table-list'}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Job Title</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Comments</th>
            {hasEditAccess && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {customerContacts.map((contact: ICustomerContact, index: number) => {
            return (
              <tr key={index}>
                <td>
                  {contact?.firstName} {contact?.lastName}
                </td>
                <td>{contact?.jobTitle}</td>
                <td>{contact?.email}</td>
                <td>{contact?.phoneNumber}</td>
                <td>
                  <div>{contact.comment ? <img className={'img-icon'} src={checkGreenIcon} alt='' /> : null}</div>
                </td>
                {hasEditAccess && <td>
                  <div className={'td-center'} style={{ gap: 10 }}>
                    <Button type={'button'} onClick={() => editContact(contact)} svgIcon={pencilIcon} />
                    <Button type={'button'} onClick={() => removeContact(contact)} svgIcon={trashIcon} />
                  </div>
                </td>}

              </tr>
            );
          })}
        </tbody>
      </table>
      <div role={"row"} className={styles.noContactsMessage}>
        {customerContacts?.length === 0 && <span>There are no items to display.</span>}
        {customerContacts?.length === 0 && hasEditAccess &&

            <>
              {" "} Click {" "}
                <button
                type="button"
                onClick={() => addContact()}
                className={styles.linkText}
            >
                here
                </button>{" "} to add contact.
            </>
        }
      </div>

      <CommonModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editMode ? `Edit Contact: ${contactForModal?.firstName} ${contactForModal?.lastName}` : 'Add New Contact'}
      >
        {editingContactIndex !== undefined && (
          <CustomerContact index={editingContactIndex}
                           customerContact={contactForModal}
                           hideDeleteButton={true} edit={true} />
        )}

        <div className={styles.modalFooter}>
          <Button type='button' onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button type='button' form='main-customer-form' themeColor={'primary'} onClick={handleSaveChanges} disabled={!isModalFormValid}>
            Save
          </Button>
        </div>
      </CommonModal>
    </>
  );
};

export default CustomerDetailsContacts;
